import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Button from 'components/Button'
import Link from 'components/Link'
import MarkdownRenderer from 'components/MarkdownRenderer'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'

import { Title26 } from 'components/Typography'
import { Spacing50 } from 'styles/sharedStyle'
import { useComponentId, useFieldId } from 'utils/optimizely'
import { fixAudiences } from '../../utils/audiences'
import {
  Body,
  Eyebrow,
  Grid,
  Heading,
  Headline,
  Inner,
  Promo,
  PromoBody,
  PromoCopy,
  PromoImg,
  Wrapper,
} from './PortraitGrid.style'

const PortraitGrid = ({
  audiences,
  bodyCopy,
  eyebrow,
  headline,
  promos,
  villageSlug,
}) => {
  const hasAudiences = !!audiences
  const [screenWidth, setScreenWidth] = useState(0)

  useEffect(() => {
    setScreenWidth(window.innerWidth)
    window.addEventListener('resize', () => setScreenWidth(window.innerWidth))
    return () => {
      window.removeEventListener('resize', () =>
        setScreenWidth(window.innerWidth)
      )
    }
  }, [])

  return (
    <Wrapper hasAudience={hasAudiences} className={fixAudiences(audiences)}>
      <Spacing50 />
      <Inner>
        {[eyebrow, headline, bodyCopy].some((s) => !!s) && (
          <Heading>
            {!!eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {!!headline && <Headline>{headline}</Headline>}
            {!!bodyCopy && <Body as={MarkdownRenderer} html={bodyCopy} />}
          </Heading>
        )}
        {!!promos && promos.length > 0 && (
          <Grid>
            {promos
              .slice(0, 4)
              .map(
                (
                  {
                    audiences: itemAudiences,
                    headline: itemHeadline,
                    bodyCopy: itemBodyCopy,
                    ctaCopy,
                    ctaUrl,
                    image: {
                      altText,
                      desktop: { gatsbyImageData: imgDesktop = {} } = {},
                      mobile: { gatsbyImageData: imgMobile = {} } = {},
                    } = {},
                  },
                  i
                ) => {
                  const elementId = useComponentId(`Comp_promo_copy_cta_PRO01`)
                  return (
                    <Promo
                      key={`promo${i.toString()}`}
                      hasAudience={!!itemAudiences}
                      className={itemAudiences}
                      onClick={() =>
                        GA4('grid_portrait_click', {
                          headline: itemHeadline,
                          component_headline: headline,
                          village_name: villageSlug,
                        })
                      }>
                      {(!!imgDesktop || !!imgMobile) && (
                        <PromoImg
                          image={screenWidth > 900 ? imgDesktop : imgMobile}
                          alt={altText}
                          promosNumber={promos.length}
                        />
                      )}
                      <PromoBody>
                        {!!itemHeadline && (
                          <Title26 id={useFieldId(elementId, 'headline')}>
                            {itemHeadline}
                          </Title26>
                        )}
                        {!!itemBodyCopy && (
                          <PromoCopy
                            id={useFieldId(elementId, 'copy')}
                            as={MarkdownRenderer}
                            html={itemBodyCopy}
                          />
                        )}
                        {!!ctaCopy && !!ctaUrl && (
                          <Button
                            id={useFieldId(elementId, 'cta')}
                            className="cta"
                            as={Link}
                            level="primary"
                            to={ctaUrl}
                            external={ctaUrl.indexOf('http') === 0}
                            onClick={() => {
                              trackGTM(
                                'cta interaction',
                                'cta interaction',
                                'in page cta',
                                ctaCopy
                              )
                            }}>
                            {ctaCopy}
                          </Button>
                        )}
                      </PromoBody>
                    </Promo>
                  )
                }
              )}
          </Grid>
        )}
      </Inner>
      <Spacing50 />
    </Wrapper>
  )
}

const markdownProptype = PropTypes.shape({
  childMarkdownRemark: PropTypes.shape({
    html: PropTypes.string,
  }),
})

PortraitGrid.propTypes = {
  audiences: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  bodyCopy: markdownProptype,
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      audiences: PropTypes.string,
      bodyCopy: markdownProptype,
      ctaCopy: PropTypes.string,
      ctaUrl: PropTypes.string,
      headline: PropTypes.string,
      image: PropTypes.shape({
        altText: PropTypes.string,
        desktop: PropTypes.shape({
          fixed: PropTypes.shape({
            src: PropTypes.string,
            srcWebp: PropTypes.string,
          }),
        }),
        mobile: PropTypes.shape({
          fixed: PropTypes.shape({
            src: PropTypes.string,
            srcWebp: PropTypes.string,
          }),
        }),
      }),
    })
  ),
  villageSlug: PropTypes.string,
}

PortraitGrid.defaultProps = {
  audiences: '',
  bodyCopy: null,
  eyebrow: '',
  headline: '',
  promos: [],
  villageSlug: '',
}

export default PortraitGrid
